import axios from "axios";
import axiosClient from "../../../utils/axiosFactory";
import { AffiliateTheme } from "../../../affiliate/types";

export type AffiliateStatus = "active" | "suspended";

export interface AffiliateSummary {
  affiliateId: number;
  slug: string;
  name: string;
  status: AffiliateStatus;
  hostname: string | null;
  activeBrandingVersion: number | null;
  createdAt: string;
  updatedAt: string | null;
}

export interface AffiliateDetail {
  affiliateId: number;
  slug: string;
  name: string;
  status: AffiliateStatus;
  hostname: string | null;
  createdAt: string;
  updatedAt: string | null;
  activeBrandingVersion: number | null;
  activeBrandingUrl: string | null;
  activeBrandingPublishedAt: string | null;
  activeTheme: AffiliateTheme | null;
}

/** Text fields of a branding version; asset URLs are always generated by the server. */
export interface BrandingDraft {
  tagline: string;
  termsUrl: string;
  supportEmail: string;
  primary: string;
  background: string;
  text: string;
}

export interface PublishBrandingResult {
  version: number;
  brandingUrl: string;
  publishedAt: string;
  warnings: string[];
}

const BASE_PATH = "/api/v1/admin/affiliates";

export const affiliateAdminApi = {
  async list(): Promise<AffiliateSummary[]> {
    return (await axiosClient().get<AffiliateSummary[]>(BASE_PATH)).data;
  },

  async get(affiliateId: number): Promise<AffiliateDetail> {
    return (await axiosClient().get<AffiliateDetail>(`${BASE_PATH}/${affiliateId}`)).data;
  },

  async create(slug: string, name: string): Promise<AffiliateDetail> {
    return (await axiosClient().post<AffiliateDetail>(BASE_PATH, { slug, name })).data;
  },

  /** An empty hostname clears it, leaving the affiliate reachable only through ?affiliate={slug}. */
  async update(
    affiliateId: number,
    changes: { name?: string; status?: AffiliateStatus; hostname?: string }
  ): Promise<void> {
    await axiosClient().patch(`${BASE_PATH}/${affiliateId}`, changes);
  },

  async publishBranding(
    affiliateId: number,
    draft: BrandingDraft,
    files: { logo: File | null; favicon: File | null; removeFavicon: boolean }
  ): Promise<PublishBrandingResult> {
    const form = new FormData();
    (Object.keys(draft) as Array<keyof BrandingDraft>).forEach((key) => form.append(key, draft[key]));
    if (files.logo) form.append("logo", files.logo);
    if (files.favicon) form.append("favicon", files.favicon);
    form.append("removeFavicon", String(files.removeFavicon));

    return (await axiosClient().post<PublishBrandingResult>(`${BASE_PATH}/${affiliateId}/branding`, form)).data;
  },
};

/** Readable message from an API ProblemDetails or ValidationProblemDetails response. */
export function apiErrorMessage(error: unknown, fallback = "Something went wrong. Please try again."): string {
  if (!axios.isAxiosError(error)) {
    return fallback;
  }

  const data = error.response?.data as { title?: string; errors?: Record<string, string[]> } | undefined;
  const validationErrors = data?.errors ? Object.values(data.errors).flat() : [];

  if (validationErrors.length > 0) {
    return validationErrors.join(" ");
  }
  return data?.title || fallback;
}
